import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const ADMIN_ROLE = "admin";
const AUTHENTICATED_ROLE = "authenticated";
const REDIRECT_URL_KEY = "auth_redirect_url";

/* Sample client principal:
  {
    claims: []
    identityProvider: "aad"
    userDetails: "test@microsoft.com"
    userId: "123abc"
    userRoles: ['anonymous', 'authenticated', 'admin']
  }
*/

export type ClientPrincipal = {
    claims: any[];
    identityProvider: string;
    userDetails: string;
    userId: string;
    userRoles: string[];
};

export type AuthData = {
    clientPrincipal: ClientPrincipal | null;
    mapReadAccess: boolean;
    isAdmin: boolean;
    isAuthenticated: boolean;
    loading: boolean;
    login: (returnUrl?: string) => void;
};

const useAuth = () => {
    const [clientPrincipal, setClientPrincipal] = useState(null);
    const [mapReadAccess, setMapReadAccess] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const location = useLocation();
    const navigate = useNavigate();

    // Function to handle login with redirect
    const login = (returnUrl?: string) => {
        // Store the current path or provided returnUrl for redirecting back after login
        const urlToStore = returnUrl || location.pathname;
        console.debug(`Storing redirect URL: ${urlToStore}`);
        localStorage.setItem(REDIRECT_URL_KEY, urlToStore);
        window.location.href = "/.auth/login/aad";
    };

    useEffect(() => {
        (async function () {
            const result = await (await fetch("/.auth/me")).json();
            const clientPrincipal = result.clientPrincipal;
            const isAdminTemp = IsAdmin(clientPrincipal);
            const isAuthenticatedTemp = IsAuthenticated(clientPrincipal);

            setMapReadAccess(isAdminTemp);
            setClientPrincipal(clientPrincipal);
            setIsAdmin(isAdminTemp);
            setIsAuthenticated(isAuthenticatedTemp);
            setLoading(false);
        })();
    }, []);

    // Separate effect to handle redirect AFTER authentication is checked
    useEffect(() => {
        // Only run this effect after authentication check is completed
        if (loading) return;
        
        // Check for redirect URL and navigate if user is authenticated
        if (isAuthenticated && location.pathname === '/') {
            const redirectUrl = localStorage.getItem(REDIRECT_URL_KEY);
            
            if (redirectUrl && redirectUrl !== '/') {
                localStorage.removeItem(REDIRECT_URL_KEY);
                navigate(redirectUrl);
            }
        }
    }, [isAuthenticated, loading, navigate, location.pathname]);

    return {
        clientPrincipal: clientPrincipal,
        mapReadAccess: mapReadAccess,
        isAdmin: isAdmin,
        isAuthenticated: isAuthenticated,
        loading: loading,
        login: login,
    };
};

// Function to handle login with redirect
export const handleLogin = (e: React.MouseEvent) => {
    e.preventDefault();
    localStorage.setItem(REDIRECT_URL_KEY, window.location.pathname);
    window.location.href = "/.auth/login/aad";
};

const IsAuthenticated = (clientPrincipal: ClientPrincipal | null) => {
    const userRoles = clientPrincipal?.userRoles || [];
    return userRoles.includes(AUTHENTICATED_ROLE);
    // anonymous and authenticated role are given to any signed in user by Static Web App
};

const IsAdmin = (clientPrincipal: ClientPrincipal | null) => {
    const userRoles = clientPrincipal?.userRoles || [];
    return userRoles.includes(ADMIN_ROLE);
};

interface TemplateProps {
    children: React.ReactNode;
}

// This is the template for when auth data is loaded
export const AuthLoadedTemplate: React.FC<TemplateProps> = ({ children }) => {
    const authData = useAuth();
    return <>{!authData?.loading && <>{children}</>}</>;
};

// This is the template for when auth data is loaded and user is not authenticated yet - usually it means user is not signed in
export const UnauthenticatedTemplate: React.FC<TemplateProps> = ({
    children,
}) => {
    const authData = useAuth();
    return (
        <>
            {!authData?.loading && !authData?.isAuthenticated && (
                <>{children}</>
            )}
        </>
    );
};

// This is the template for when user is authenticated - usually it means user is signed in
export const AuthenticatedTemplate: React.FC<TemplateProps> = ({
    children,
}) => {
    const authData = useAuth();
    return <>{authData?.isAuthenticated && <>{children}</>}</>;
};

// This is the template for when auth data is loaded and user is not authorized for read access - usually it means user doesn't have proper read access to view some content, like mapbox, data, charts
export const ReadAccessUnauthorizedTemplate: React.FC<TemplateProps> = ({
    children,
}) => {
    const authData = useAuth();
    return <>{!authData?.loading && !authData?.isAdmin && <>{children}</>}</>;
};

// This is the template for when user is authorized for read access - usually it means user has proper read access to view some content, like mapbox, data, charts
export const ReadAccessAuthorizedTemplate: React.FC<TemplateProps> = ({
    children,
}) => {
    const authData = useAuth();
    return <>{authData?.isAdmin && <>{children}</>}</>;
};

export default useAuth;